import { useMemo } from 'react';
import { DAY_MS } from '../../../utils/onlineActivity';
import { daysUntil } from '../../../utils/assignmentDeadlines';
import useSmoText from '../smoText';

/**
 * On-screen versions of utils/onlineActivity's formatters and of
 * describeDeadline, in the selected language. Same thresholds; the Excel
 * exports keep using the Turkish originals.
 */
const useActivityFormat = () => {
  const { label, locale } = useSmoText();
  return useMemo(() => ({
    /** 5400000 → "1 sa 30 dk" / "1 h 30 min" */
    duration: (ms) => {
      if (!ms || ms <= 0) return '—';
      const totalMinutes = Math.floor(ms / 60000);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      if (hours > 0) return label('time.hm', { h: hours, m: minutes });
      if (minutes > 0) return label('time.m', { m: minutes });
      return label('time.s', { s: Math.max(1, Math.floor(ms / 1000)) });
    },
    /** "3 saat önce" */
    relative: (ms) => {
      if (!ms) return label('time.noActivity');
      const diff = Date.now() - ms;
      if (diff < 60000) return label('time.justNow');
      if (diff < 3600000) return label('time.minutesAgo', { count: Math.floor(diff / 60000) });
      if (diff < DAY_MS) return label('time.hoursAgo', { count: Math.floor(diff / 3600000) });
      return label('time.daysAgo', { count: Math.floor(diff / DAY_MS) });
    },
    /** "12.08.2026 14:30" */
    dateTime: (ms) => (ms
      ? new Date(ms).toLocaleString(locale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      : '—'),
    /** "2 gün kaldı" / "bugün son gün" / "3 gün gecikti" */
    deadline: (dueDate) => {
      const days = daysUntil(dueDate);
      if (days === null) return label('deadline.none');
      if (days < 0) return label('deadline.overdue', { count: Math.abs(days) });
      if (days === 0) return label('deadline.today');
      if (days === 1) return label('deadline.lastDay');
      return label('deadline.left', { count: days });
    },
  }), [label, locale]);
};

export default useActivityFormat;
